import { useToast } from '@/components/ui/use-toast';
import { LoginPage } from '@/graphql/generated/graphql';
import { useAuthentication, User } from '@/hooks/useAuthentication';
import { getLoginPage, login } from '@/services/auth/auth.service';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import * as z from 'zod';

const formSchema = z.object({
  identifier: z.string().min(1, { message: 'Identifier is required' }),
  password: z.string().min(1, { message: 'Password is required' }),
  rememberMe: z.boolean().optional(),
});

export type FormInputs = z.infer<typeof formSchema>;

export default function useLogin() {
  const [loginPage, setLoginPage] = useState<LoginPage | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const {
    handleSubmit,
    reset,
    register,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    resolver: zodResolver(
      z.object({
        identifier: z
          .string()
          .min(1, { message: loginPage?.fieldErrors?.identifierRequired || 'Identifier is required' }),
        password: z.string().min(1, { message: loginPage?.fieldErrors?.passwordRequired || 'Password is required' }),
        rememberMe: z.boolean().optional(),
      }),
    ),
    mode: 'onBlur',
  });

  const { loginAuth } = useAuthentication();
  const location = useLocation();
  const isConfirmation = location.pathname.includes('confirmed');
  const queryParams = new URLSearchParams(location.search);
  const callbackUrl = queryParams.get('callbackUrl');

  const onLogin = async (data: {
    identifier: string;
    password: string;
    rememberMe?: boolean;
    errorMessage:
      | {
          title?: string | null;
          message?: string | null;
        }
      | undefined
      | null;
  }) => {
    const result = await login({
      email: data.identifier,
      password: data.password,
      rememberMe: data.rememberMe || false,
    });
    if (result.error) {
      toast({
        title: data?.errorMessage?.title || 'Error signing in',
        description: data?.errorMessage?.message || result.error.message,
        variant: 'destructive',
      });
      return;
    }

    // Set authentication
    await loginAuth(result.data!);
    reset();

    const user = result.data.user as User;

    if (user.onboardHomeCompleted) {
      if (callbackUrl && callbackUrl !== '/not-authorized') {
        navigate(callbackUrl);
        return;
      }
      navigate('/');
      return;
    }

    if (user?.ws_permission?.isAdmin || user?.ws_permission?.isMAT) {
      navigate('/admin-on-boarding/step-1');
      return;
    }

    const currentStep = localStorage.getItem(`current-step-${user?.id}`);
    navigate(currentStep || '/on-boarding/step-1');
  };

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    return await onLogin({
      identifier: data.identifier,
      password: data.password,
      rememberMe: data.rememberMe || false,
      errorMessage: loginPage?.popUpMessages?.error,
    });
  };

  useEffect(() => {
    (async () => {
      const [loginPageResponse] = await Promise.all([getLoginPage()]);
      setLoginPage(loginPageResponse.page as LoginPage);
    })();
  }, []);

  return {
    loginPage,
    isConfirmation,
    handleSubmit: handleSubmit(onSubmit),
    register,
    setValue,
    onLogin,
    errors,
  };
}
